import { tokenSelector } from "@/libs/auth";
import { useAppSelector } from "@/services/store";
import md5 from "crypto-js/md5";
import * as React from "react";
import useWebSocket, { Options } from "react-use-websocket";
// Sometimes we will have a circular dependency due to shouldReconnect using return value from
// useWebSocketWithToken, where we'll need to set type manually.
// This is just a nicer single import we can use in other files.
export type { WebSocketHook } from "react-use-websocket/dist/lib/types";

export enum CloseEventCodes {
  NotFound = 4004,
  Unauthorized = 4010,
  Forbidden = 4030,
  BadRequest = 4000,
}

export function defaultShouldReconnect(event: CloseEvent) {
  return (
    event.code !== CloseEventCodes.NotFound &&
    event.code !== CloseEventCodes.Unauthorized &&
    event.code !== CloseEventCodes.Forbidden
  );
}

// Naive approach to check if the message is a failure message.
// Most messages should follow this format, but we need something more robust in the future.
function isFailureMessage<T = unknown>(message: T | { type: string }) {
  return typeof message === "object" && message !== null && "type" in message && message.type === "error";
}

// react-use-websocket has reconnectAttempts, however, it only applies to the initial connection.
// where as we open the connection, send the error and then re-connect.
// This means that the library will just keep on trying to reconnect over and over again.
// causing a mess in our logs
// Going forward we need to re-design our approach and only use 1 socket per page with
// message ids to track the messages, insead of opening a new socket for each task.
// This way we don't need to keep closing and opening sockets.
const MAX_FAILED_COUNT = 10;
/**
 * Wrapper around useWebsocket, which fixes the token
 * not being passed correctly through protocols.
 * We get around this by hashing the token and adding it to queryParams.
 */
export function useWebSocketWithToken<T extends { type: string }>(
  token: string,
  url: string,
  { shouldReconnect, ...options }: Omit<Options, "protocols" | "queryParams">,
  connect?: boolean,
) {
  const failedCount = React.useRef(0);
  const tokenHash = React.useMemo(() => md5(token).toString(), [token]);
  const socket = useWebSocket<T>(
    url,
    {
      protocols: ["accesstoken", token ?? ""],
      queryParams: { tokenHash },
      shouldReconnect(event) {
        if (failedCount.current >= MAX_FAILED_COUNT) {
          console.error("WebSocket failed to reconnect too many times, stopping attempts.");
          return false;
        }
        const fn = shouldReconnect ?? defaultShouldReconnect;
        return fn(event);
      },
      ...options,
    },
    connect,
  );

  React.useEffect(() => {
    if (isFailureMessage(socket.lastJsonMessage)) {
      failedCount.current += 1;
    } else {
      failedCount.current = 0;
    }
  }, [socket.lastJsonMessage]);

  return socket;
}

export function useAuthenticatedWebSocket<T extends { type: string }>(
  url: string,
  options?: Omit<Options, "protocols" | "queryParams">,
  connect?: boolean,
) {
  const token = useAppSelector(tokenSelector);
  return useWebSocketWithToken<T>(token, url, options ?? {}, connect);
}
